import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Clock, CheckCircle2 } from "lucide-react";

const SLOTS = ["14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00", "21:00"];

export default function ArrivalPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/public/arrival/${id}`).then((r) => {
      setData(r.data); setTime(r.data.arrival_time || ""); setNotes(r.data.arrival_notes || "");
    }).catch((e) => setError(formatApiError(e.response?.data?.detail)));
  }, [id]);

  const submit = async () => {
    if (!time) { setError("Seleziona un orario"); return; }
    setSaving(true); setError("");
    try {
      const r = await api.post(`/public/arrival/${id}`, { arrival_time: time, arrival_notes: notes });
      setData(r.data); setDone(true);
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="arrival-page">
      <Card className="w-full max-w-md p-8 border-border shadow-md animate-fade-up">
        {!data && !error && <p className="text-muted-foreground text-center">Caricamento…</p>}
        {error && !data && <p className="text-destructive text-center" data-testid="arrival-error">{error}</p>}
        {data && (
          <>
            <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">{data.structure_name}</p>
            <h1 className="font-display text-3xl sm:text-4xl mt-1">Orario di arrivo</h1>
            <p className="text-muted-foreground mt-2 text-sm">Gentile {data.guest_name}, ci indichi quando prevede di arrivare.</p>
            <div className="mt-5 bg-secondary/50 rounded-lg p-4 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Camera</span><span className="font-medium">{data.room_name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Check-in</span><span className="font-medium">{fmtDate(data.check_in)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Check-out</span><span className="font-medium">{fmtDate(data.check_out)}</span></div>
            </div>
            {done ? (
              <div className="mt-6 text-center space-y-2" data-testid="arrival-success">
                <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
                <p className="font-heading font-semibold">Grazie! Arrivo previsto alle {data.arrival_time}</p>
                <p className="text-sm text-muted-foreground">La aspettiamo. A presto!</p>
                <Button variant="outline" onClick={() => setDone(false)} data-testid="arrival-edit">Modifica</Button>
              </div>
            ) : (
              <div className="mt-6 space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Fascia oraria</Label>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {SLOTS.map((s) => (
                      <button key={s} type="button" data-testid={`slot-${s.replace(":", "")}`} onClick={() => setTime(s)}
                        className={`text-sm py-2 rounded-lg border transition-colors ${time === s ? "bg-accent text-accent-foreground border-accent" : "border-border hover:bg-secondary"}`}>{s}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Oppure orario preciso</Label>
                  <div className="relative mt-1.5">
                    <Clock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input type="time" data-testid="arrival-time-input" className="pl-9" value={time} onChange={(e) => setTime(e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Note (opzionale)</Label>
                  <Textarea data-testid="arrival-notes" className="mt-1.5" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Es. arrivo in treno, servirebbe un parcheggio…" />
                </div>
                {error && <p className="text-sm text-destructive" data-testid="arrival-error">{error}</p>}
                <Button data-testid="arrival-submit" onClick={submit} disabled={saving} className="w-full bg-accent hover:bg-accent/90 text-accent-foreground rounded-full h-11">
                  {saving ? "Invio…" : "Conferma orario di arrivo"}
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
